import React, { useEffect, Suspense } from 'react';
import { isCustomBookingHost } from './utils/customBookingHost';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ThemedToaster from './components/ui/ThemedToaster';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { loadUser } from './store/slices/authSlice';

// Statically imported — needed on first paint or for auth/theme/socket setup
import ForceLightTheme from './components/ForceLightTheme';
import PrivateRoute from './components/PrivateRoute';
import RoleBasedRouter from './components/RoleBasedRouter';
import { SocketProvider } from './context/SocketContext';
import SocketNotificationListener from './components/SocketNotificationListener';
import VersionUpdateNotifier from './components/VersionUpdateNotifier';
import SupportChat from './components/SupportChat';
// lazyWithRetry: like React.lazy, but auto-recovers from post-deploy chunk 404s.
import lazyWithRetry from './utils/lazyWithRetry';

// Route-level lazy imports — each lands in its own chunk
const Login = lazyWithRetry(() => import('./pages/Login'));
const ApproveQuotation = lazyWithRetry(() => import('./pages/ApproveQuotation'));
const PublicQuotationView = lazyWithRetry(() => import('./pages/PublicQuotationView'));
const PublicInvoiceView = lazyWithRetry(() => import('./pages/PublicInvoiceView'));
const PublicReceiptView = lazyWithRetry(() => import('./pages/PublicReceiptView'));
const PublicPaymentPage = lazyWithRetry(() => import('./pages/PublicPaymentPage'));
const PaymentSuccess = lazyWithRetry(() => import('./pages/PaymentSuccess'));
const PaymentError = lazyWithRetry(() => import('./pages/PaymentError'));
const DownpaymentPayment = lazyWithRetry(() => import('./pages/DownpaymentPayment'));
const BookAppointment = lazyWithRetry(() => import('./pages/BookAppointment'));
const BookingManage = lazyWithRetry(() => import('./pages/BookingManage'));
const TenantRedirect = lazyWithRetry(() => import('./pages/TenantRedirect'));
const ConfirmIntake = lazyWithRetry(() => import('./pages/ConfirmIntake'));
const PublicTerms = lazyWithRetry(() => import('./pages/PublicTerms'));
const PublicOnboarding = lazyWithRetry(() => import('./pages/PublicOnboarding'));
const PublicFeedback = lazyWithRetry(() => import('./pages/PublicFeedback'));
const SubscriptionEnded = lazyWithRetry(() => import('./pages/SubscriptionEnded'));
const VerifyCertificate = lazyWithRetry(() => import('./pages/VerifyCertificate'));

// Public customer routes must ALWAYS render light — the Suspense fallback shows
// before ForceLightTheme mounts, so it must not follow the stored/system theme.
const PUBLIC_PATH_PREFIXES = ['/quotations/', '/q/', '/public/', '/payment/', '/downpayment/', '/book', '/book/manage/', '/confirm/', '/terms/', '/onboarding/', '/f/', '/t/'];

// The host rule lives in utils/customBookingHost (tested there).
export { isCustomBookingHost } from './utils/customBookingHost';

// Minimal Suspense fallback — reuses the same spinner pattern as the auth loading screen
function PageFallback() {
  // The verify host is all public — same light spinner as the public paths.
  const isVerify = window.location.hostname.split('.')[0] === 'verify';
  const isPublicPath =
    isVerify ||
    isCustomBookingHost() ||
    PUBLIC_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix));
  if (isPublicPath) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-gray-500" />
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-surface-3 border-t-accent" />
    </div>
  );
}

function App() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // `initializing`, NOT `loading`. `loading` is true during ANY auth request,
  // including a login submit, and this component swaps the entire Router for a
  // spinner while it is true — so submitting an OTP unmounted the login screen
  // and remounted it fresh when the request settled, throwing away the step and
  // the typed number. `initializing` covers only the startup loadUser.
  const { initializing } = useSelector((state) => state.auth);
  
  // Check if we're on a public subdomain. Match the FIRST label so this holds on
  // app.motormind.autos AND app.dev.motormind.autos ('app' is a reserved
  // subdomain), plus verify.motormind.autos — the public certificate
  // verification page. Both skip loadUser: neither host has a tenant session.
  const firstLabel = window.location.hostname.split('.')[0];
  const isVerifySubdomain = firstLabel === 'verify';
  const isPublicSubdomain = firstLabel === 'app' || isVerifySubdomain;

  useEffect(() => {
    // Only load user if not on public subdomain
    if (!isPublicSubdomain) {
      dispatch(loadUser());
    }
  }, [dispatch, isPublicSubdomain]);

  // Platform-wide: stop the mouse wheel from silently changing a focused
  // <input type="number"> value (a common cause of accidental quantity/price
  // edits). We only block the value change when the focused number input is the
  // one under the wheel — page scrolling everywhere else is untouched. Must be a
  // non-passive listener so preventDefault() takes effect.
  useEffect(() => {
    const onWheel = (e) => {
      const el = document.activeElement;
      if (
        el &&
        el.tagName === 'INPUT' &&
        el.type === 'number' &&
        (el === e.target || el.contains(e.target))
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => document.removeEventListener('wheel', onWheel, { passive: false });
  }, []);

  if (initializing && !isPublicSubdomain) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-canvas gap-6">
        <img src="/assets/logo.png" alt="MotorMind" className="w-28 h-28 object-contain motion-safe:animate-pulse" />
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-surface-3 border-t-accent"></div>
      </div>
    );
  }

  // The public certificate verification host (verify.<domain>): one page, no
  // tenant session, no sockets, no support widget — nothing but the verifier.
  if (isVerifySubdomain) {
    return (
      <Router>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route
              path="*"
              element={
                <ForceLightTheme>
                  <VerifyCertificate />
                </ForceLightTheme>
              }
            />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  return (
    <>
      <Router>
        <SocketProvider>
          <SocketNotificationListener />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Public routes - no authentication required; always render in light theme */}
              <Route path="/quotations/:action/:token" element={<ForceLightTheme><PublicQuotationView /></ForceLightTheme>} />
              <Route path="/q/:token" element={<ForceLightTheme><PublicQuotationView /></ForceLightTheme>} />
              <Route path="/public/invoices/:token" element={<ForceLightTheme><PublicInvoiceView /></ForceLightTheme>} />
              {/* Short alias for SMS: /i/<token> is 14 characters shorter than
                  /public/invoices/<token>, and Arabic SMS bills per 70 chars.
                  The long path stays forever — links already sent must not break. */}
              <Route path="/i/:token" element={<ForceLightTheme><PublicInvoiceView /></ForceLightTheme>} />
              <Route path="/public/receipts/:token" element={<ForceLightTheme><PublicReceiptView /></ForceLightTheme>} />
              <Route path="/payment/success" element={<ForceLightTheme><PaymentSuccess /></ForceLightTheme>} />
              <Route path="/payment/error" element={<ForceLightTheme><PaymentError /></ForceLightTheme>} />
              <Route path="/payment/:token" element={<ForceLightTheme><PublicPaymentPage /></ForceLightTheme>} />
              <Route path="/downpayment/:token" element={<ForceLightTheme><DownpaymentPayment /></ForceLightTheme>} />
              <Route path="/book" element={<ForceLightTheme><BookAppointment /></ForceLightTheme>} />
              <Route path="/book/p/:slug" element={<ForceLightTheme><BookAppointment /></ForceLightTheme>} />
              <Route path="/book/b/:brand" element={<ForceLightTheme><BookAppointment /></ForceLightTheme>} />
              <Route path="/book/b/:brand/p/:slug" element={<ForceLightTheme><BookAppointment /></ForceLightTheme>} />
              {isCustomBookingHost() && (
                <>
                  <Route path="/" element={<ForceLightTheme><BookAppointment /></ForceLightTheme>} />
                  <Route path="/p/:slug" element={<ForceLightTheme><BookAppointment /></ForceLightTheme>} />
                </>
              )}
              <Route path="/book/manage/:token" element={<ForceLightTheme><BookingManage /></ForceLightTheme>} />
              <Route path="/t/:sub" element={<ForceLightTheme><TenantRedirect /></ForceLightTheme>} />
              <Route path="/confirm/:token" element={<ForceLightTheme><ConfirmIntake /></ForceLightTheme>} />
              <Route path="/terms/:garageId" element={<ForceLightTheme><PublicTerms /></ForceLightTheme>} />
              <Route path="/onboarding/:token" element={<ForceLightTheme><PublicOnboarding /></ForceLightTheme>} />
              <Route path="/f/:token" element={<ForceLightTheme><PublicFeedback /></ForceLightTheme>} />

              {/* The locked-out-tenant wall. Listed with the public routes on
                  purpose: the tenant still HAS a valid session, but every API
                  call 402s, so it must render without any authenticated data. */}
              <Route path="/subscription-ended" element={<SubscriptionEnded />} />

              {/* Login route - only for tenant subdomains, not app subdomain */}
              {!isPublicSubdomain && (
                <Route path="/login" element={<Login />} />
              )}

              {/* Protected routes - require authentication on tenant subdomains */}
              {/* Public portal on app subdomain - show error for unauthorized access */}
              <Route
                path="/*"
                element={
                  isPublicSubdomain ? (
                    <div className="min-h-screen flex items-center justify-center bg-canvas">
                      <div className="text-center">
                        <h1 className="text-6xl font-bold text-content-muted/40 mb-4">403</h1>
                        <h2 className="text-2xl font-semibold text-content">{t('cm.app.accessDenied')}</h2>
                      </div>
                    </div>
                  ) : (
                    <PrivateRoute>
                      {/* Inside PrivateRoute on purpose. The public routes above
                          are opened by the GARAGE's customers following an SMS
                          link — they are not Tzamun's customers and must not be
                          handed a Tzamun support widget. Renders nothing; the
                          launcher is the header help button. */}
                      <SupportChat />
                      <RoleBasedRouter />
                    </PrivateRoute>
                  )
                }
              />
            </Routes>
          </Suspense>
        </SocketProvider>
      </Router>
      <ThemedToaster />
      <VersionUpdateNotifier />
    </>
  );
}

export default App;
